import { useCallback, useMemo, useState } from 'react';
import { usePersistentStorage } from '../../../../lib/storage';
import { changeOnboardingStage, changeOnboardingSubtask, useGetOnboardingStages } from '../../api/api';
import { ONBOARDING_FINISHED_STORAGE_KEY } from '../../api/constants';
import type { OnboardingStage, OnboardingSubtask } from '../../api/types';

export const useOnboardingStage = (stage: OnboardingStage, { skip = false }: { skip?: boolean } = {}) => {
    const { data } = useGetOnboardingStages(undefined, { skip });

    const [isFinished] = usePersistentStorage<'1'>(ONBOARDING_FINISHED_STORAGE_KEY);

    const stageData = useMemo(() => data?.stages.find((s) => s.slug === stage), [data, stage]);
    const [isLoading, setIsLoading] = useState(false);

    const complete = useCallback(async () => {
        // если nullish, то не шлем запрос
        if (stageData?.is_completed !== false) {
            return;
        }

        try {
            setIsLoading(true);
            await changeOnboardingStage({
                slug: stage,
                value: true,
            });
        } finally {
            setIsLoading(false);
        }
    }, [stage, stageData]);

    const changeSubtask = useCallback(
        async (subtaskSlug: OnboardingSubtask, newValue: boolean) => {
            if (
                // если nullish, то не шлем запрос
                !stageData?.subtasks.some(
                    (subtask) => subtask.slug === subtaskSlug && subtask.is_completed === !newValue
                )
            ) {
                return;
            }

            try {
                setIsLoading(true);
                await changeOnboardingSubtask({
                    slug: subtaskSlug,
                    value: newValue,
                });
            } finally {
                setIsLoading(false);
            }
        },
        [stageData]
    );

    const completeSubtask = useCallback(
        (subtaskSlug: OnboardingSubtask) => changeSubtask(subtaskSlug, true),
        [changeSubtask]
    );

    const resetSubtask = useCallback(
        (subtaskSlug: OnboardingSubtask) => changeSubtask(subtaskSlug, false),
        [changeSubtask]
    );

    const isCompleted = useMemo(() => Boolean(stageData?.is_completed || isFinished === '1'), [stageData, isFinished]);

    const getIsSubtaskCompleted = useCallback(
        (subtaskSlug: OnboardingSubtask) => {
            if (isCompleted) {
                return true;
            }

            return Boolean(stageData?.subtasks.find((subtask) => subtask.slug === subtaskSlug)?.is_completed);
        },
        [stageData, isCompleted]
    );

    return {
        stage: stageData,
        isCompleted,
        isLoading,
        complete,
        completeSubtask,
        resetSubtask,
        getIsSubtaskCompleted,
    };
};
